import React, { useState, useEffect } from 'react';
import scheduleAssignmentService, { type CreateAssignmentInput } from '../services/scheduleAssignmentService';
import scheduleTemplateService, { type ScheduleTemplate } from '../services/scheduleTemplateService';
import channelService, { type Channel } from '../services/channelService';

interface AssignTemplateModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess: () => void;
  preselectedChannelId?: string;
  preselectedStartTime?: string; // ISO date string from calendar click
  preselectedTemplateId?: string; // Template to assign
}

const formatDateTimeLocalInput = (date: Date) => {
  const tzOffset = date.getTimezoneOffset() * 60000;
  return new Date(date.getTime() - tzOffset).toISOString().slice(0, 16);
};

const AssignTemplateModal: React.FC<AssignTemplateModalProps> = ({
  isOpen,
  onClose,
  onSuccess,
  preselectedChannelId,
  preselectedStartTime,
  preselectedTemplateId,
}) => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [checkingConflict, setCheckingConflict] = useState(false);
  const [conflict, setConflict] = useState<any>(null);
  const [useSnapToEnd, setUseSnapToEnd] = useState(true);

  // Form data
  const [channelId, setChannelId] = useState(preselectedChannelId || '');
  const [templateId, setTemplateId] = useState(preselectedTemplateId || '');
  const [startTime, setStartTime] = useState(
    preselectedStartTime
      ? formatDateTimeLocalInput(new Date(preselectedStartTime))
      : formatDateTimeLocalInput(new Date())
  );
  const [notes, setNotes] = useState('');
  const [snapEndTime, setSnapEndTime] = useState<Date | null>(null);

  // Data lists
  const [channels, setChannels] = useState<Channel[]>([]);
  const [templates, setTemplates] = useState<ScheduleTemplate[]>([]);
  const [selectedTemplate, setSelectedTemplate] = useState<ScheduleTemplate | null>(null);
  const [calendarSelectionTime, setCalendarSelectionTime] = useState<Date | null>(
    preselectedStartTime ? new Date(preselectedStartTime) : null
  );
  const [calendarSelectionAccepted, setCalendarSelectionAccepted] = useState<boolean>(
    preselectedStartTime ? false : true
  );

  // Load channels and templates
  useEffect(() => {
    if (isOpen) {
      loadChannels();
      loadTemplates();
    }
  }, [isOpen]);

  // Update selected template when templateId changes
  useEffect(() => {
    if (templateId && templates.length > 0) {
      const template = templates.find((t) => t.id === templateId);
      setSelectedTemplate(template || null);
    } else {
      setSelectedTemplate(null);
    }
  }, [templateId, templates]);

  useEffect(() => {
    if (isOpen && preselectedStartTime) {
      const selection = new Date(preselectedStartTime);
      setCalendarSelectionTime(selection);
      setCalendarSelectionAccepted(false);
      setStartTime(formatDateTimeLocalInput(selection));
      setUseSnapToEnd(false);
    } else if (isOpen && !preselectedStartTime) {
      setCalendarSelectionTime(null);
      setCalendarSelectionAccepted(true);
      setStartTime(formatDateTimeLocalInput(new Date()));
      setUseSnapToEnd(true);
    }
  }, [isOpen, preselectedStartTime]);

  // Load snap-to-end time when channel and date change
  useEffect(() => {
    if (channelId && startTime && useSnapToEnd) {
      loadSnapEndTime();
    } else {
      setSnapEndTime(null);
    }
  }, [channelId, startTime, useSnapToEnd]);

  // Check for conflicts when channel, template, or start time changes
  useEffect(() => {
    if (channelId && templateId && startTime && selectedTemplate) {
      checkConflict();
    }
  }, [channelId, templateId, startTime, selectedTemplate, snapEndTime]);

  const loadChannels = async () => {
    try {
      const response = await channelService.listChannels({ limit: 100 });
      setChannels(response.channels);
    } catch (err) {
      console.error('Error loading channels:', err);
    }
  };

  const loadTemplates = async () => {
    try {
      const response = await scheduleTemplateService.list({ limit: 100 });
      setTemplates(response.templates);
    } catch (err) {
      console.error('Error loading templates:', err);
    }
  };

  const loadSnapEndTime = async () => {
    if (!channelId || !startTime) return;

    try {
      const date = new Date(startTime).toISOString().split('T')[0]; // Get YYYY-MM-DD
      const result = await scheduleAssignmentService.getLastEndTime(channelId, date);

      if (result) {
        setSnapEndTime(new Date(result));
      } else {
        setSnapEndTime(null);
      }
    } catch (err) {
      console.error('Error loading snap end time:', err);
      setSnapEndTime(null);
    }
  };

  const checkConflict = async () => {
    if (!channelId || !selectedTemplate || !startTime) return;

    try {
      setCheckingConflict(true);

      const effectiveStartTime = useSnapToEnd && snapEndTime ? snapEndTime : new Date(startTime);

      const result = await scheduleAssignmentService.checkConflict({
        channel_id: channelId,
        schedule_template_id: selectedTemplate.id,
        start_time: effectiveStartTime.toISOString(),
      });

      setConflict(result.has_conflict ? result.conflicting_assignment : null);
    } catch (err) {
      console.error('Error checking conflict:', err);
    } finally {
      setCheckingConflict(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!channelId || !templateId || !startTime) {
      setError('Please fill in all required fields');
      return;
    }

    if (calendarSelectionTime && !calendarSelectionAccepted && !useSnapToEnd) {
      setError('Please accept the selected start time from the calendar or adjust it manually.');
      return;
    }

    if (conflict) {
      setError('Please resolve the conflict before creating the assignment');
      return;
    }

    try {
      setLoading(true);
      setError(null);

      // Use snap-to-end time if enabled and available
      const effectiveStartTime = useSnapToEnd && snapEndTime ? snapEndTime.toISOString() : new Date(startTime).toISOString();

      const data: CreateAssignmentInput = {
        schedule_template_id: templateId,
        channel_id: channelId,
        start_time: effectiveStartTime,
      };

      if (notes.trim()) {
        data.notes = notes.trim();
      }

      await scheduleAssignmentService.createAssignment(data);
      onSuccess();
      handleClose();
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to create assignment');
      console.error('Error creating assignment:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    setChannelId(preselectedChannelId || '');
    setTemplateId(preselectedTemplateId || '');
    setStartTime(formatDateTimeLocalInput(new Date()));
    setNotes('');
    setError(null);
    setConflict(null);
    setSnapEndTime(null);
    setUseSnapToEnd(true);
    setCalendarSelectionTime(null);
    setCalendarSelectionAccepted(true);
    onClose();
  };

  if (!isOpen) return null;

  const duration = selectedTemplate?.total_duration || 0;
  const effectiveStart = useSnapToEnd && snapEndTime ? snapEndTime : new Date(startTime);
  const endTime = effectiveStart ? new Date(effectiveStart.getTime() + duration * 1000) : null;

  const formatDuration = (seconds: number) => {
    const hours = Math.floor(seconds / 3600);
    const minutes = Math.floor((seconds % 3600) / 60);
    const secs = Math.floor(seconds % 60);

    if (hours > 0) {
      return `${hours}h ${minutes}m`;
    } else if (minutes > 0) {
      return `${minutes}m ${secs}s`;
    } else {
      return `${secs}s`;
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg max-w-2xl w-full max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-gray-200">
          <h2 className="text-2xl font-bold text-gray-900">Assign Template to Channel</h2>
          <button
            onClick={handleClose}
            className="text-gray-400 hover:text-gray-600 transition-colors"
          >
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="p-6">
          {/* Error Message */}
          {error && (
            <div className="mb-4 bg-red-50 border border-red-200 rounded-lg p-4">
              <p className="text-red-800 text-sm">{error}</p>
            </div>
          )}

          {/* Conflict Warning */}
          {conflict && (
            <div className="mb-4 bg-yellow-50 border border-yellow-200 rounded-lg p-4">
              <div className="flex items-start gap-2">
                <svg className="w-5 h-5 text-yellow-600 flex-shrink-0 mt-0.5" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                </svg>
                <div className="flex-1">
                  <p className="font-medium text-yellow-800 mb-1">Schedule Conflict Detected</p>
                  <p className="text-sm text-yellow-700">
                    Another assignment is already scheduled during this time slot.
                  </p>
                  {conflict.template_name && (
                    <p className="text-sm text-yellow-600 mt-1">
                      Conflicting: "{conflict.template_name}" ({new Date(conflict.start_time).toLocaleString()})
                    </p>
                  )}
                </div>
              </div>
            </div>
          )}

          {/* Snap-to-End Info */}
          {useSnapToEnd && snapEndTime && (
            <div className="mb-4 bg-blue-50 border border-blue-200 rounded-lg p-4">
              <div className="flex items-start gap-2">
                <svg className="w-5 h-5 text-blue-600 flex-shrink-0 mt-0.5" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
                </svg>
                <div className="flex-1">
                  <p className="font-medium text-blue-800 mb-1">Snap-to-End Enabled</p>
                  <p className="text-sm text-blue-700">
                    This will start at {snapEndTime.toLocaleString()} (after the last scheduled assignment)
                  </p>
                </div>
              </div>
            </div>
          )}

          {/* Checking Conflict */}
          {checkingConflict && (
            <div className="mb-4 bg-blue-50 border border-blue-200 rounded-lg p-4">
              <p className="text-blue-800 text-sm flex items-center gap-2">
                <svg className="animate-spin h-4 w-4" fill="none" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                </svg>
                Checking for conflicts...
              </p>
            </div>
          )}

          {/* Channel Selection */}
          <div className="mb-4">
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Channel <span className="text-red-500">*</span>
            </label>
            <select
              value={channelId}
              onChange={(e) => setChannelId(e.target.value)}
              required
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            >
              <option value="">Select a channel</option>
              {channels.map((channel) => (
                <option key={channel.id} value={channel.id}>
                  {channel.name} ({channel.status})
                </option>
              ))}
            </select>
          </div>

          {/* Template Selection */}
          <div className="mb-4">
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Schedule Template <span className="text-red-500">*</span>
            </label>
            <select
              value={templateId}
              onChange={(e) => setTemplateId(e.target.value)}
              required
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            >
              <option value="">Select a template</option>
              {templates.map((template) => (
                <option key={template.id} value={template.id}>
                  {template.name} ({template.video_count} videos, {formatDuration(template.total_duration)})
                </option>
              ))}
            </select>
            {selectedTemplate && (
              <p className="text-xs text-gray-500 mt-1">
                Videos: {selectedTemplate.video_count} | Total Duration: {formatDuration(selectedTemplate.total_duration)}
              </p>
            )}
          </div>

          {/* Start Time */}
          <div className="mb-4">
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Start Time <span className="text-red-500">*</span>
            </label>
            <input
              type="datetime-local"
              value={startTime}
              onChange={(e) => {
                setStartTime(e.target.value);
                setCalendarSelectionAccepted(true);
              }}
              required
              disabled={useSnapToEnd && !!snapEndTime}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent disabled:bg-gray-100 disabled:cursor-not-allowed"
            />
            {calendarSelectionTime && (
              <div className="mt-2 flex flex-wrap items-center gap-3 text-xs text-gray-600 bg-gray-50 border border-gray-200 rounded-lg px-3 py-2">
                <div>
                  Calendar selection:{' '}
                  <span className="font-semibold text-gray-900">{calendarSelectionTime.toLocaleString()}</span>
                </div>
                {!calendarSelectionAccepted && (
                  <button
                    type="button"
                    onClick={() => {
                      if (calendarSelectionTime) {
                        setStartTime(formatDateTimeLocalInput(calendarSelectionTime));
                        setCalendarSelectionAccepted(true);
                        setUseSnapToEnd(false);
                      }
                    }}
                    className="inline-flex items-center gap-1 px-2 py-1 bg-blue-100 text-blue-700 rounded-full font-medium"
                  >
                    Accept Selected Time
                    <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                    </svg>
                  </button>
                )}
                {calendarSelectionAccepted && !useSnapToEnd && (
                  <span className="inline-flex items-center gap-1 px-2 py-1 text-green-700 bg-green-100 rounded-full font-medium">
                    <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                    </svg>
                    Accepted
                  </span>
                )}
              </div>
            )}
            {endTime && (
              <p className="text-xs text-gray-500 mt-1">
                Effective Start: {effectiveStart.toLocaleString()}<br />
                End Time: {endTime.toLocaleString()} (Duration: {formatDuration(duration)})
              </p>
            )}
          </div>

          {/* Snap-to-End Toggle */}
          {snapEndTime && (
            <div className="mb-4">
              <label className="flex items-center gap-2 cursor-pointer">
                <input
                  type="checkbox"
                  checked={useSnapToEnd}
                  onChange={(e) => {
                    setUseSnapToEnd(e.target.checked);
                    if (e.target.checked) {
                      setCalendarSelectionAccepted(true);
                    }
                  }}
                  className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-2 focus:ring-blue-500"
                />
                <span className="text-sm text-gray-700">
                  Auto-start after last assignment (Snap-to-End)
                </span>
              </label>
            </div>
          )}

          {/* Notes (Optional) */}
          <div className="mb-6">
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Notes
              <span className="text-gray-500 font-normal ml-2">Optional</span>
            </label>
            <textarea
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              rows={3}
              maxLength={1000}
              placeholder="Add any notes about this assignment..."
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent resize-none"
            />
            <p className="text-xs text-gray-500 mt-1">{notes.length}/1000 characters</p>
          </div>

          {/* Actions */}
          <div className="flex gap-3">
            <button
              type="button"
              onClick={handleClose}
              className="flex-1 px-6 py-2.5 border border-gray-300 rounded-lg text-gray-700 font-medium hover:bg-gray-50 transition-colors"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={loading || checkingConflict || !!conflict || !channelId || !templateId}
              className="flex-1 px-6 py-2.5 bg-blue-600 hover:bg-blue-700 text-white font-medium rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
            >
              {loading ? (
                <>
                  <svg className="animate-spin h-4 w-4" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                  </svg>
                  Creating...
                </>
              ) : (
                'Assign Template'
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default AssignTemplateModal;
